import React, { createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react'
import { cn } from './cn'

/**
 * App 面的清單（design-guideline §3.5、§7.4）：白底、列與列之間一條髮絲線，一列兩行，
 * 群組標是 12px 灰字。給「很多筆的東西」用：語音輸入歷史、詞典、設定檔。
 *
 * 有 `selection` 時是單選 listbox：整個清單只佔一個 Tab 停點，↑↓／Home／End 同時移動
 * 焦點與選取（跟 ViewTabs 一樣，焦點跟著選取走）。Enter、Delete、Esc 要做什麼是頁面的
 * 事（§7.7），由 `onKeyDown` 交給頁面；只有清單列本身發出的按鍵會交出去。沒有
 * `selection` 時每列是普通的連結或靜態列。
 *
 * 列層級的次要動作是右鍵選單，看得見的入口是欄標題右上的「…」（§7.7），不是列裡的按鈕：
 * 列的 `onContextMenu` 呼叫 `useContextMenu().open`，`<ContextMenu>` 放在 `List` 旁邊，
 * 不放進列裡。所以 `ListRow` 的 `end` 只放非互動內容（數量、赭石警示、狀態字）：列內的
 * 按鈕會多出 Tab 停點，option 也不該包互動控制。
 *
 * 不能選取的清單（沒有 `selection`，例如設定檔內容欄的「在這些地方生效」）每列本來就各自
 * 停 Tab，列尾可以放一個互動控制（移除的 ×），走 `action`；可選取的清單不收 `action`。
 *
 * 游標跟 `Button` 一致：點了會換右邊內容的列（有 `selection`）、連結列、有 `onClick` 的列
 * 是手指；純展示的列（右鍵選單不算）維持箭頭。有 `onClick` 的列整列是一顆按鈕（跟連結列
 * 同一種結構），所以 Tab 到得了、有聚焦外框；`action` 留在按鈕外面。
 *
 * 有 `preview` 的列（一段話＋一行時間字數）上下內距、預覽行高、時間的上距各自讀
 * `--*-preview` token，跟只有標題的列分開。
 *
 * 預設清單鋪滿整欄：`ul` 只留左邊的 gutter，列的右邊用 padding 補，框線與滑過的底色畫到
 * 欄的右緣。`inset` 讓清單縮在內容欄內（左右各一個 gutter，跟頁面其他區塊的分隔線對齊），
 * 最後一列下面收一條線（滑過時跟其他列一樣隱藏）；滑過的底色往外多一個 `--gap-sm`，圓角
 * `--radius-sm`，文字仍對齊內容欄。整列是按鈕的列與連結列，鍵盤焦點框也往外多一個
 * `--gap-sm`（畫在按鈕／連結外面）；`selection` 清單的列焦點是列自己，焦點框維持內縮，
 * 目前沒有呼叫端同時用 `inset` 與 `selection`。
 */

export interface ListSelection {
  value: string | null
  onChange: (value: string) => void
}

interface SelectionState extends ListSelection {
  /** The row holding the single tab stop: the selected one if rendered, else the first. */
  tabStop: string | null
}

/** Shared with `VirtualList`, whose rows are the same `ListRow`s. */
export const SelectionContext = createContext<SelectionState | null>(null)

/** Set by `List`'s `inset`; read by its `ListGroup`s and `ListRow`s. */
const InsetContext = createContext(false)

export interface ListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  'aria-label': string
  selection?: ListSelection
  /**
   * Sit inside the content column (a gutter on both sides, a rule under the last
   * row) instead of spanning it. Default: spans the column.
   */
  inset?: boolean
  /** One or more `ListGroup`s. */
  children: React.ReactNode
}

export const MOVE_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End'])

/**
 * The row a move key lands on, from row `from` (-1 when none has focus) in a
 * list whose last row is `last`. ↑↓ stop at the ends instead of wrapping.
 */
export function nextRowIndex(key: string, from: number, last: number): number {
  if (key === 'Home') return 0
  if (key === 'End') return last
  return Math.min(last, Math.max(0, from + (key === 'ArrowDown' ? 1 : -1)))
}

export function List({ selection, inset = false, onKeyDown, children, ...rest }: ListProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  // The tab stop is the selected row only while that row is on screen; a value
  // filtered out or just deleted would otherwise leave every row at -1 and the
  // list unreachable by Tab. Rows do not know their index, so both facts are
  // read from the DOM after it commits.
  const [tabStop, setTabStop] = useState<string | null>(null)
  useLayoutEffect(() => {
    if (!selection) return
    const options = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []
    )
    const values = options.map((option) => option.dataset.value)
    const next =
      selection.value !== null && values.includes(selection.value)
        ? selection.value
        : (values[0] ?? null)
    setTabStop((current) => (current === next ? current : next))
  })

  const move = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const options = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []
    )
    if (options.length === 0) return
    const from = options.findIndex((option) => option.contains(event.target as Node))
    const next = nextRowIndex(event.key, from, options.length - 1)
    event.preventDefault()
    const target = options[next]
    target.focus()
    const value = target.dataset.value
    if (value !== undefined && value !== selection?.value) selection?.onChange(value)
  }

  return (
    <SelectionContext.Provider value={selection ? { ...selection, tabStop } : null}>
      <InsetContext.Provider value={inset}>
        <div
          ref={rootRef}
          role={selection ? 'listbox' : undefined}
          {...rest}
          onKeyDown={(event) => {
            if (!selection) {
              onKeyDown?.(event)
              return
            }
            // Only a row's own keys belong to the list and the page. Anything
            // focusable that ended up inside a row keeps its keys: React bubbles
            // them here along the component tree (portals included), and an Enter
            // or Delete meant for that control must not open or delete the row.
            if ((event.target as HTMLElement).getAttribute('role') !== 'option') return
            if (MOVE_KEYS.has(event.key)) move(event)
            else onKeyDown?.(event)
          }}
        >
          {children}
        </div>
      </InsetContext.Provider>
    </SelectionContext.Provider>
  )
}

export interface GroupHeadingProps extends React.HTMLAttributes<HTMLDivElement> {
  /** For a virtual list's measuring. */
  ref?: React.Ref<HTMLDivElement>
  children: React.ReactNode
  /** Right-hand note, e.g.「保留 7 天」. */
  aside?: React.ReactNode
  /** Id on the label alone, so a group named by it is not also named by the aside. */
  labelId?: string
}

/** 群組標：12px 灰字，不用 mono、不全大寫、不拉字距。也用在設定列群組的上方。 */
export function GroupHeading({ children, aside, labelId, className, ...rest }: GroupHeadingProps) {
  return (
    <div
      {...rest}
      className={cn(
        'flex items-baseline justify-between gap-[var(--gap-sm)] px-[var(--m-gutter)] pb-1.5 pt-7 text-xs text-muted',
        className
      )}
    >
      <span id={labelId}>{children}</span>
      {aside != null ? <span>{aside}</span> : null}
    </div>
  )
}

export interface ListGroupProps {
  /** 群組標（今天、昨天）；沒有就只是一段清單。 */
  label?: React.ReactNode
  aside?: React.ReactNode
  children: React.ReactNode
}

export function ListGroup({ label, aside, children }: ListGroupProps) {
  const selectable = useContext(SelectionContext) !== null
  const inset = useContext(InsetContext)
  const headingId = useId()
  const hasLabel = label != null
  return (
    <>
      {hasLabel ? (
        <GroupHeading labelId={headingId} aside={aside}>
          {label}
        </GroupHeading>
      ) : null}
      <ul
        role={selectable ? 'group' : undefined}
        aria-labelledby={hasLabel ? headingId : undefined}
        className={cn('m-0 list-none p-0', inset ? GROUP_INSET : 'pl-[var(--m-gutter)]')}
      >
        {children}
      </ul>
    </>
  )
}

export interface ListRowProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** The row's `<li>`, e.g. for a sortable list's node ref. */
  ref?: React.Ref<HTMLLIElement>
  /** Required inside a List with `selection`. */
  value?: string
  title?: React.ReactNode
  /** 設定類清單用一般字重（預設 600）。 */
  regularTitle?: boolean
  /** 兩行截斷的內文。 */
  preview?: React.ReactNode
  /** 12px 灰字 tabular，項目之間插「·」。 */
  meta?: React.ReactNode[]
  lead?: React.ReactNode
  /**
   * Non-interactive trailing content only: a count, an ochre mark, a status
   * word. A row's actions live in the right-click menu and the column header's
   * 「…」, not in a button here.
   */
  end?: React.ReactNode
  /** 整列是連結（沒有 selection 的清單）。 */
  href?: string
  /** 停用中的東西：標題轉灰、字重 500（設定檔清單的「已停用」）。 */
  muted?: boolean
  /**
   * One interactive control at the row's end (a remove ×), for lists WITHOUT
   * `selection` only. A selectable list's rows are options, which must not hold
   * controls; there it is refused (not rendered, and reported in the console).
   */
  action?: React.ReactNode
}

/**
 * An inset list's group: a gutter on both sides, the last row closed by a rule.
 * Only the bottom rule is coloured here: `border-color` would set the top rule too,
 * at a higher specificity than the rows' own `hover:border-t-transparent` and
 * `[&:hover+li]:border-t-transparent`, and those rules above the last row would
 * stay while it or its neighbour is hovered. Like any other row, the last one
 * clears its rule on hover and its fill covers that rule's pixel.
 */
const GROUP_INSET =
  'mx-[var(--m-gutter)] [&>li:last-child]:border-b [&>li:last-child]:border-b-[color:var(--line)] ' +
  '[&>li:last-child:hover]:border-b-transparent [&>li:last-child]:before:-bottom-px'

const FOCUS =
  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--focus-ring)]'

/**
 * A row's own focus target (the link or the button) in an inset list: its box is
 * widened by one gap on both sides, text padding makes up the difference, and the
 * outline is drawn around that box.
 */
const FOCUS_INSET =
  '-mx-[var(--gap-sm)] rounded-[var(--radius-sm)] px-[var(--gap-sm)] focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[var(--focus-ring)]'

/**
 * Hover and selected fills are painted on ::before, from the list's left edge
 * (one gutter left of the row) to the column's right edge, so the row itself
 * never shifts and the rules above and below it never jump. A filled row hides
 * its own top rule and the next row's.
 *
 * `isolate` makes every row its own stacking context, so a menu opened from one
 * row's `action` would paint under the next row and lose its clicks to that
 * row's buttons; the row with an open popover (`aria-expanded`) is lifted.
 */
const ROW =
  'relative isolate has-[[aria-expanded=true]]:z-10 flex min-h-[var(--min-h-row)] items-center gap-[var(--gap-sm)] border-t border-[color:var(--line)] text-foreground ' +
  'before:pointer-events-none before:absolute before:-top-px before:bottom-0 before:-z-10 ' +
  'hover:border-t-transparent hover:before:bg-[var(--fg-soft)] [&:hover+li]:border-t-transparent ' +
  'aria-selected:border-t-transparent aria-selected:before:bg-brand-soft aria-selected:before:shadow-[inset_2px_0_0_var(--brand)] [&[aria-selected=true]+li]:border-t-transparent'

/** Spans the column: the right gutter is the row's padding, the fill runs out to the column edge. */
const ROW_FULL = 'pr-[var(--m-gutter)] before:left-[calc(-1*var(--m-gutter))] before:right-0'

/** Inset: the row ends at the column's gutter; only the fill reaches one gap past the rule. */
const ROW_INSET =
  'before:left-[calc(-1*var(--gap-sm))] before:right-[calc(-1*var(--gap-sm))] before:rounded-[var(--radius-sm)]'

export function ListRow({
  value,
  title,
  regularTitle = false,
  preview,
  meta,
  lead,
  end,
  href,
  muted = false,
  action,
  className,
  onClick,
  ...rest
}: ListRowProps) {
  const selection = useContext(SelectionContext)
  const inset = useContext(InsetContext)
  const row = cn(ROW, inset ? ROW_INSET : ROW_FULL)
  const focus = inset ? FOCUS_INSET : FOCUS
  if (selection && action != null) {
    console.error(
      'ListRow: `action` is for lists without `selection`; a selectable row is an option and cannot hold a control. Use the right-click menu and the column header「…」instead.'
    )
  }

  const hasPreview = preview != null
  const pad = hasPreview ? 'py-[var(--m-row-pad-preview)]' : 'py-[var(--m-row-pad)]'

  const body = (
    <>
      {lead != null ? (
        <span className="flex h-8 min-w-8 shrink-0 items-center justify-center gap-1 text-foreground">
          {lead}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        {title != null ? (
          <span
            className={cn(
              'block truncate text-[length:var(--fs-m-row)] leading-[1.4]',
              muted ? 'font-medium text-muted' : regularTitle ? 'font-normal' : 'font-semibold'
            )}
          >
            {title}
          </span>
        ) : null}
        {preview != null ? (
          <span className="line-clamp-2 text-md leading-[var(--leading-row-preview)]">
            {preview}
          </span>
        ) : null}
        {meta && meta.length > 0 ? (
          <span
            className={cn(
              'block truncate text-xs tabular-nums text-muted',
              hasPreview ? 'mt-[var(--gap-row-meta-preview)]' : 'mt-0.5'
            )}
          >
            {meta.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 ? (
                  <span aria-hidden="true" className="mx-1.25 opacity-60">
                    ·
                  </span>
                ) : null}
                <span>{item}</span>
              </React.Fragment>
            ))}
          </span>
        ) : null}
      </span>
      {end != null ? (
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted">{end}</span>
      ) : null}
    </>
  )
  const trailing =
    action != null && !selection ? (
      <span className="flex shrink-0 items-center">{action}</span>
    ) : null
  const content = (
    <>
      {body}
      {trailing}
    </>
  )

  if (selection) {
    const selected = value !== undefined && value === selection.value
    return (
      <li
        {...rest}
        role="option"
        aria-selected={selected}
        data-value={value}
        tabIndex={value !== undefined && value === selection.tabStop ? 0 : -1}
        className={cn(row, pad, 'cursor-pointer', FOCUS, className)}
        onClick={(event) => {
          if (value !== undefined && !selected) selection.onChange(value)
          onClick?.(event)
        }}
      >
        {content}
      </li>
    )
  }

  if (href !== undefined) {
    return (
      <li {...rest} className={cn(row, 'cursor-default', className)} onClick={onClick}>
        <a
          href={href}
          className={cn(
            'flex min-h-[inherit] flex-1 cursor-pointer items-center gap-[var(--gap-sm)] text-inherit no-underline',
            pad,
            focus
          )}
        >
          {content}
        </a>
      </li>
    )
  }

  if (onClick) {
    // The whole row is one button, so the keyboard reaches it; a trailing
    // control stays beside the button, never inside it.
    return (
      <li {...rest} className={cn(row, 'cursor-default', className)}>
        <button
          type="button"
          className={cn(
            'flex min-h-[inherit] min-w-0 flex-1 cursor-pointer items-center gap-[var(--gap-sm)] bg-transparent text-left text-inherit',
            pad,
            focus
          )}
          onClick={onClick}
        >
          {body}
        </button>
        {trailing}
      </li>
    )
  }

  return (
    <li {...rest} className={cn(row, pad, 'cursor-default', className)}>
      {content}
    </li>
  )
}
